import axios, { AxiosHeaders, isAxiosError, type InternalAxiosRequestConfig } from 'axios';
import { DEVICE_HEADER, HEADERS } from '@capsico/contracts';
import { attachIntegrity, integrityRetry } from './integrityState';
import { recordErrorRef } from './lastErrorRef';
import { deviceId } from './device';

const MUTATING = new Set(['post', 'put', 'patch']);

/**
 * The one HTTP client for the customer app. In the browser the session is an HttpOnly cookie
 * (JavaScript never sees it). Every request carries this device's id (one vote per device, new-device alerts)
 * and every mutation an X-Idempotency-Key.
 */
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? 'http://localhost:4000',
  withCredentials: true,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const headers = AxiosHeaders.from(config.headers);
  headers.set(DEVICE_HEADER, deviceId());
  attachIntegrity(headers);
  if (config.method && MUTATING.has(config.method.toLowerCase())) {
    if (!headers.has(HEADERS.IDEMPOTENCY_KEY)) {
      headers.set(HEADERS.IDEMPOTENCY_KEY, crypto.randomUUID());
    }
  }
  config.headers = headers;
  return config;
});

const unauthorizedListeners = new Set<() => void>();

/** Any 401 outside the sign-in routes means the session ended (expired or signed out elsewhere). */
export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

api.interceptors.response.use(undefined, (error: unknown) => {
  if (isAxiosError(error) && error.response?.status === 401) {
    const url = error.config?.url ?? '';
    if (!url.startsWith('/v1/auth/')) {
      for (const listener of unauthorizedListeners) listener();
    }
  }
  return Promise.reject(error);
});

/** W24: a sensitive call refused for a missing / stale integrity pass gets one fresh check + retry. */
api.interceptors.response.use(undefined, integrityRetry(api));

/** W25: remember the support code of the latest failure for the generic error screens. */
api.interceptors.response.use(undefined, (error: unknown) => {
  recordErrorRef(errorRef(error));
  return Promise.reject(error);
});

export function errorStatus(error: unknown): number | null {
  return isAxiosError(error) ? (error.response?.status ?? null) : null;
}

export function errorMessage(
  error: unknown,
  fallback = 'Something went wrong. Try again.',
): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
    if (!error.response) return 'No internet connection. Check your network and try again.';
  }
  return fallback;
}

const SUPPORT_REF = /^CAP-[0-9A-HJKMNP-TV-Z]{6}$/;

/**
 * W25: the short support code (`CAP-7K2Q9M`) of a failed API call — from the error body, else the
 * `x-capsico-ref` header. Null when absent or malformed (older servers send neither).
 */
export function errorRef(error: unknown): string | null {
  if (!isAxiosError(error) || !error.response) return null;
  const body = error.response.data as { error?: { ref?: unknown } } | undefined;
  const fromBody = body?.error?.ref;
  if (typeof fromBody === 'string' && SUPPORT_REF.test(fromBody)) return fromBody;
  const header: unknown = error.response.headers?.['x-capsico-ref'];
  if (typeof header === 'string' && SUPPORT_REF.test(header)) return header;
  return null;
}
